import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  TextField,
  Autocomplete,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Avatar,
  Skeleton,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import {
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Timer as TimerIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  PersonSearch as PersonSearchIcon,
  DirectionsCar as DirectionsCarIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  Directions as DirectionsIcon,
  Group as GroupIcon,
  Notes as NotesIcon,
  Repeat as RepeatIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { calendarioService } from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Componente StatBox riutilizzabile
const StatBox = ({ icon, value, label }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      mb: 0.5
    }}>
      {icon && React.cloneElement(icon, { sx: { color: 'rgba(255,255,255,0.7)', fontSize: '1.2rem' } })}
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
      {label}
    </Typography>
  </Box>
);

const Calendario = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [personaFilter, setPersonaFilter] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const calendarRef = useRef(null);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);

  useEffect(() => {
    loadCalendario();
  }, []);

  const loadCalendario = async () => {
    try {
      setLoading(true);
      const dataInizio = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
      const dataFine = dayjs().add(3, 'month').format('YYYY-MM-DD');

      const response = await calendarioService.getEventi({
        data_inizio: dataInizio,
        data_fine: dataFine,
      });

      setEventi(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del calendario:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo.event);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleEditIntervento = () => {
    if (selectedEvent && selectedEvent.extendedProps.id) {
      handleCloseDetailDialog();
      navigate('/interventi', {
        state: {
          editInterventoId: selectedEvent.extendedProps.id
        }
      });
    }
  };

  const handleViewIntervento = () => {
    if (selectedEvent && selectedEvent.extendedProps.id) {
      handleCloseDetailDialog();
      navigate(`/interventi/${selectedEvent.extendedProps.id}`);
    }
  };

  const handleGestisciTimbrature = () => {
    if (selectedEvent && selectedEvent.extendedProps.id) {
      handleCloseDetailDialog();
      navigate('/timbrature', {
        state: {
          interventoId: selectedEvent.extendedProps.id
        }
      });
    }
  };

  const handleGetDirections = () => {
    if (selectedEvent) {
      const sedeOrigin = '45.4761692,8.63074';
      const destination = encodeURIComponent(
        selectedEvent.extendedProps.cantiere_indirizzo || selectedEvent.extendedProps.cantiere
      );
      const url = `https://www.google.com/maps/dir/?api=1&origin=${sedeOrigin}&destination=${destination}`;
      window.open(url, '_blank');
    }
  };

  const getStatoStyle = (stato) => {
    const styles = {
      programmato: { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196f3', border: '#2196f3', icon: <CalendarIcon /> },
      in_corso: { bg: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', border: '#ff9800', icon: <PlayArrowIcon /> },
      completato: { bg: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', border: '#4caf50', icon: <CheckCircleIcon /> },
    };
    return styles[stato] || { bg: 'rgba(158, 158, 158, 0.1)', color: '#9e9e9e', border: '#9e9e9e', icon: null };
  };

  // Colori allineati a quelli degli eventi del calendario (impostati dal backend:
  // manutenzione = blu #007bff, impianto = verde #28a745). Vedi anche la legenda.
  const getTipologiaStyle = (tipologia) => {
    return tipologia === 'manutenzione'
      ? { bg: 'rgba(0, 123, 255, 0.12)', color: '#007bff' }
      : { bg: 'rgba(40, 167, 69, 0.12)', color: '#28a745' };
  };

  const getExportData = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return null;

    const view = api.view;
    const viewStart = dayjs(view.activeStart);
    const viewEnd = dayjs(view.activeEnd);

    const visibleEvents = filteredEventi.filter((evento) => {
      const eventDate = evento.extendedProps?.data || evento.start;
      if (!eventDate) return false;
      const d = dayjs(eventDate);
      return d.isAfter(viewStart.subtract(1, 'day')) && d.isBefore(viewEnd);
    });

    visibleEvents.sort((a, b) => {
      const dateA = a.extendedProps?.data || a.start || '';
      const dateB = b.extendedProps?.data || b.start || '';
      if (dateA !== dateB) return dateA < dateB ? -1 : 1;
      const oraA = a.extendedProps?.ora_inizio || '';
      const oraB = b.extendedProps?.ora_inizio || '';
      return oraA < oraB ? -1 : 1;
    });

    const columns = ['Data', 'Orario', 'Cantiere', 'Cliente', 'Capocantiere', 'Tecnici', 'Targa Mezzo', 'Tipologia', 'Stato'];

    const rows = visibleEvents.map((evento) => {
      const p = evento.extendedProps || {};
      const tecnici = p.tecnici?.map((t) => t.nome).join(', ') || '-';
      return [
        p.data ? dayjs(p.data).format('DD/MM/YYYY') : '-',
        p.ora_inizio && p.ora_fine ? `${p.ora_inizio} - ${p.ora_fine}` : '-',
        p.cantiere || '-',
        p.cliente || '-',
        p.capocantiere || '-',
        tecnici,
        p.targa_mezzo || '-',
        p.tipologia || '-',
        p.stato?.replace('_', ' ') || '-',
      ];
    });

    const dateRangeStr = `${viewStart.format('DD/MM/YYYY')} - ${viewEnd.subtract(1, 'day').format('DD/MM/YYYY')}`;
    const filterLabel = personaFilter ? `Filtro persona: ${personaFilter.nome}` : '';

    return { columns, rows, dateRangeStr, filterLabel, count: rows.length };
  };

  const handlePrint = () => {
    setExportMenuAnchorEl(null);
    const data = getExportData();
    if (!data || data.count === 0) return;

    const printWindow = window.open('', '_blank');
    const rowsHtml = data.rows
      .map(
        (row, i) =>
          `<tr style="background-color: ${i % 2 === 0 ? '#ffffff' : '#f9f9f9'}">
            ${row.map((cell) => `<td style="border: 1px solid #ddd; padding: 6px 8px; font-size: 12px;">${cell}</td>`).join('')}
          </tr>`
      )
      .join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Calendario Interventi</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            h1 { font-size: 18px; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 13px; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th { background-color: #1a1a1a; color: white; padding: 8px; font-size: 12px; text-align: left; }
            .footer { margin-top: 20px; font-size: 11px; color: #999; border-top: 1px solid #ddd; padding-top: 8px; }
            @media print {
              body { padding: 0; }
              @page { size: landscape; margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <h1>NC Elettrica - Calendario Interventi</h1>
          <p class="subtitle">Periodo: ${data.dateRangeStr} | ${data.count} interventi${data.filterLabel ? ` | ${data.filterLabel}` : ''}</p>
          <table>
            <thead><tr>${data.columns.map((c) => `<th>${c}</th>`).join('')}</tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
          <div class="footer">Stampato il ${dayjs().format('DD/MM/YYYY HH:mm')}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExportCSV = () => {
    setExportMenuAnchorEl(null);
    const data = getExportData();
    if (!data || data.count === 0) return;

    const bom = '\uFEFF';
    const header = data.columns.join(';');
    const csvRows = data.rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(';'));
    const csvContent = bom + [header, ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `calendario_interventi_${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    setExportMenuAnchorEl(null);
    const data = getExportData();
    if (!data || data.count === 0) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Intestazione
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('NC Elettrica - Calendario Interventi', 14, 18);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    let subtitleLine = `Periodo: ${data.dateRangeStr} | ${data.count} interventi`;
    if (data.filterLabel) subtitleLine += ` | ${data.filterLabel}`;
    doc.text(subtitleLine, 14, 25);
    doc.setTextColor(0);

    // Tabella
    autoTable(doc, {
      head: [data.columns],
      body: data.rows,
      startY: 30,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [26, 26, 26], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 26 },
        2: { cellWidth: 38 },
        3: { cellWidth: 35 },
        4: { cellWidth: 30 },
        5: { cellWidth: 38 },
        6: { cellWidth: 24 },
        7: { cellWidth: 26 },
        8: { cellWidth: 22 },
      },
      didDrawPage: (pageData) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Generato il ${dayjs().format('DD/MM/YYYY HH:mm')} - Pagina ${pageData.pageNumber} di ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 8,
          { align: 'center' }
        );
      },
    });

    doc.save(`calendario_interventi_${dayjs().format('YYYY-MM-DD')}.pdf`);
  };

  const renderEventContent = (eventInfo) => {
    const props = eventInfo.event.extendedProps;
    const tipologiaStyle = getTipologiaStyle(props.tipologia);

    // Nella cella del mese ci sono ~150px: su una riga sola sparivano fino a 272px
    // di nome cantiere e non si capiva di quale intervento si trattasse. Il nome va
    // su due righe, e il testo completo resta leggibile passandoci sopra.
    const descrizioneCompleta = [
      eventInfo.timeText,
      props.cantiere,
      props.cliente,
      props.capocantiere ? `Capocantiere: ${props.capocantiere}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <Box
        title={descrizioneCompleta}
        sx={{
          p: 0.5,
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.9,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            // FullCalendar impone white-space:nowrap e display:flow-root sui propri
            // elementi evento: senza forzare la precedenza, il ritorno a capo non
            // avviene e il line-clamp (che richiede -webkit-box) resta inattivo.
            display: '-webkit-box !important',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'normal !important',
            overflow: 'hidden',
            lineHeight: 1.25,
          }}
        >
          {eventInfo.timeText} - {props.cantiere}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {props.cliente}
        </Typography>
      </Box>
    );
  };

  // Persone impegnate negli interventi caricati (capocantiere + tecnici): opzioni della ricerca
  const persone = useMemo(() => {
    const mappa = new Map();
    eventi.forEach((evento) => {
      const p = evento.extendedProps || {};
      if (p.capocantiere_id && p.capocantiere) {
        mappa.set(p.capocantiere_id, { id: p.capocantiere_id, nome: p.capocantiere });
      }
      (p.tecnici || []).forEach((t) => {
        if (t?.id && t?.nome) mappa.set(t.id, { id: t.id, nome: t.nome });
      });
    });
    return [...mappa.values()].sort((a, b) => a.nome.localeCompare(b.nome));
  }, [eventi]);

  // Filtra gli eventi per persona: conta sia come capocantiere sia come tecnico assegnato
  const filteredEventi = personaFilter
    ? eventi.filter((evento) => {
        const p = evento.extendedProps || {};
        return (
          p.capocantiere_id === personaFilter.id ||
          (p.tecnici || []).some((t) => t.id === personaFilter.id)
        );
      })
    : eventi;

  // Statistiche
  const stats = {
    totali: eventi.length,
    programmati: eventi.filter(e => e.extendedProps?.stato === 'programmato').length,
    inCorso: eventi.filter(e => e.extendedProps?.stato === 'in_corso').length,
    completati: eventi.filter(e => e.extendedProps?.stato === 'completato').length,
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'amministratore';
  const canManage = isAdmin || user?.role === 'capocantiere';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={500} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 4 } }}>
      {/* Header moderno con sfondo scuro */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          color: 'white',
          pt: { xs: 2, sm: 4 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Pattern decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Accento rosso decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: 'radial-gradient(circle, rgba(229,0,0,0.15) 0%, transparent 70%)',
            borderRadius: '50%',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Titolo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  bgcolor: '#e50000',
                  boxShadow: '0 4px 14px rgba(229,0,0,0.4)',
                }}
              >
                <CalendarIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    letterSpacing: '-0.5px',
                  }}
                >
                  {isMobile ? 'Calendario' : 'Calendario Interventi'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                  {isAdmin
                    ? 'Vista completa di tutti gli interventi'
                    : 'I tuoi interventi programmati'}
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Esporta / Stampa">
              <IconButton
                onClick={(e) => setExportMenuAnchorEl(e.currentTarget)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportMenuAnchorEl}
              open={Boolean(exportMenuAnchorEl)}
              onClose={() => setExportMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handlePrint}>
                <ListItemIcon><PrintIcon sx={{ color: '#1a1a1a' }} /></ListItemIcon>
                <ListItemText>Stampa</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportCSV}>
                <ListItemIcon><DescriptionIcon sx={{ color: '#4caf50' }} /></ListItemIcon>
                <ListItemText>Esporta CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleExportPDF}>
                <ListItemIcon><PictureAsPdfIcon sx={{ color: '#f44336' }} /></ListItemIcon>
                <ListItemText>Esporta PDF</ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          {/* Statistiche */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 2, sm: 3 },
              mt: 3,
            }}
          >
            <StatBox
              icon={<AssignmentIcon />}
              value={stats.totali}
              label="Totali"
            />
            <StatBox
              icon={<CalendarIcon />}
              value={stats.programmati}
              label="Programmati"
            />
            <StatBox
              icon={<PlayArrowIcon />}
              value={stats.inCorso}
              label="In Corso"
            />
            <StatBox
              icon={<CheckCircleIcon />}
              value={stats.completati}
              label="Completati"
            />
          </Box>
        </Container>
      </Box>

      {/* Contenuto principale */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Ricerca per Persona - Solo per Amministratore */}
        {isAdmin && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: searchFocused ? '0 4px 20px rgba(229,0,0,0.1)' : '0 2px 12px rgba(0,0,0,0.04)',
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                <PersonSearchIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {isMobile ? 'Ricerca Persona' : 'Ricerca per Persona'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'flex-start' } }}>
              <Autocomplete
                fullWidth
                options={persone}
                value={personaFilter}
                onChange={(event, newValue) => setPersonaFilter(newValue)}
                getOptionLabel={(option) => option.nome || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="Nessuna persona con interventi in questo periodo"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Cerca una persona (capocantiere o tecnico)"
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: searchFocused ? '#e50000' : 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        '&:hover': { bgcolor: 'grey.100' },
                        '&.Mui-focused': {
                          bgcolor: 'white',
                          '& fieldset': { borderColor: '#e50000', borderWidth: 2 },
                        },
                      },
                    }}
                  />
                )}
                sx={{ flex: 1 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(33, 150, 243, 0.08)',
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  border: '1px solid rgba(33, 150, 243, 0.2)',
                  minWidth: { sm: 280 },
                }}
              >
                <GroupIcon sx={{ color: '#2196f3', fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#1565c0' }}>
                  Vedi dove e quando è impegnata una persona
                </Typography>
              </Box>
            </Box>

            {personaFilter && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${filteredEventi.length} intervento/i per "${personaFilter.nome}"`}
                  size="small"
                  sx={{
                    bgcolor: filteredEventi.length > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    color: filteredEventi.length > 0 ? '#4caf50' : '#f44336',
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}
          </Paper>
        )}

        {/* Legenda colori tipologia intervento */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2.5 },
            mb: 2,
            px: { xs: 0.5, sm: 1 },
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            Legenda:
          </Typography>
          {[
            { label: 'Manutenzione', color: getTipologiaStyle('manutenzione').color },
            { label: 'Impianto', color: getTipologiaStyle('impianto').color },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: item.color, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendario */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            '& .fc': {
              fontFamily: 'inherit',
            },
            '& .fc-toolbar-title': {
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 700,
              color: '#1a1a1a',
            },
            '& .fc-button': {
              bgcolor: '#f5f5f5 !important',
              border: '1px solid #e0e0e0 !important',
              color: '#1a1a1a !important',
              fontWeight: 600,
              borderRadius: '8px !important',
              textTransform: 'capitalize',
              '&:hover': {
                bgcolor: '#e0e0e0 !important',
              },
              '&.fc-button-active': {
                bgcolor: '#e50000 !important',
                borderColor: '#e50000 !important',
                color: 'white !important',
              },
            },
            '& .fc-today-button': {
              bgcolor: '#e50000 !important',
              borderColor: '#e50000 !important',
              color: 'white !important',
              '&:hover': {
                bgcolor: '#c50000 !important',
              },
              '&:disabled': {
                bgcolor: '#f5f5f5 !important',
                borderColor: '#e0e0e0 !important',
                color: '#9e9e9e !important',
              },
            },
            '& .fc-daygrid-day.fc-day-today': {
              bgcolor: 'rgba(229, 0, 0, 0.05) !important',
            },
            '& .fc-daygrid-day-number': {
              fontWeight: 600,
              color: '#1a1a1a',
            },
            '& .fc-event': {
              borderRadius: '6px !important',
              border: 'none !important',
              cursor: 'pointer',
            },
            '& .fc-col-header-cell': {
              bgcolor: '#f5f5f5',
              fontWeight: 700,
              color: '#1a1a1a',
              py: 1,
            },
            '& .fc-scrollgrid': {
              border: 'none !important',
            },
            '& .fc-scrollgrid td, & .fc-scrollgrid th': {
              borderColor: '#e0e0e0 !important',
            },
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={isMobile ? 'timeGridDay' : 'dayGridMonth'}
            locale={itLocale}
            headerToolbar={{
              left: 'prev,next',
              center: 'title',
              right: isMobile ? 'today' : 'dayGridMonth,timeGridWeek,timeGridDay today',
            }}
            events={filteredEventi}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            height={isMobile ? 'auto' : 'auto'}
            slotMinTime="06:00:00"
            slotMaxTime="20:00:00"
            weekends={true}
            dayMaxEvents={isMobile ? 2 : 3}
            eventDisplay="block"
            displayEventTime={true}
            displayEventEnd={true}
            contentHeight="auto"
            aspectRatio={isMobile ? 1 : 1.8}
            allDaySlot={false}
            slotDuration="01:00:00"
            expandRows={true}
          />
        </Paper>
      </Container>

      {/* Dialog Dettaglio Evento */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        {selectedEvent && (
          <>
            {/* Header Dialog */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                color: 'white',
                p: 3,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: getTipologiaStyle(selectedEvent.extendedProps.tipologia).bg,
                      color: getTipologiaStyle(selectedEvent.extendedProps.tipologia).color,
                    }}
                  >
                    <BuildIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedEvent.extendedProps.cantiere}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        icon={React.cloneElement(getStatoStyle(selectedEvent.extendedProps.stato).icon, { sx: { fontSize: '0.9rem !important' } })}
                        label={selectedEvent.extendedProps.stato?.replace('_', ' ')}
                        sx={{
                          bgcolor: getStatoStyle(selectedEvent.extendedProps.stato).bg,
                          color: getStatoStyle(selectedEvent.extendedProps.stato).color,
                          border: `1px solid ${getStatoStyle(selectedEvent.extendedProps.stato).border}`,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          '& .MuiChip-icon': { color: getStatoStyle(selectedEvent.extendedProps.stato).color },
                        }}
                      />
                      <Chip
                        size="small"
                        label={selectedEvent.extendedProps.tipologia}
                        sx={{
                          bgcolor: getTipologiaStyle(selectedEvent.extendedProps.tipologia).bg,
                          color: getTipologiaStyle(selectedEvent.extendedProps.tipologia).color,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      />
                      {selectedEvent.extendedProps.ricorrente && (
                        <Chip
                          size="small"
                          icon={<RepeatIcon sx={{ fontSize: '0.9rem !important' }} />}
                          label="Ricorrente"
                          variant="outlined"
                          sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetailDialog} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Info rapide */}
              <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {dayjs(selectedEvent.extendedProps.data).format('dddd DD MMMM YYYY')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScheduleIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedEvent.extendedProps.ora_inizio} - {selectedEvent.extendedProps.ora_fine}
                  </Typography>
                </Box>
                {selectedEvent.extendedProps.targa_mezzo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DirectionsCarIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {selectedEvent.extendedProps.targa_mezzo}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Cliente e Cantiere */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                      <BusinessIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Cliente
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEvent.extendedProps.cliente}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                      <LocationIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Indirizzo Cantiere
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEvent.extendedProps.cantiere_indirizzo || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Capocantiere */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                      <PersonIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Capocantiere
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEvent.extendedProps.capocantiere || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Tempo Viaggio */}
                {selectedEvent.extendedProps.tempo_viaggio_andata && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                        <TimerIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Tempo di Viaggio
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedEvent.extendedProps.tempo_viaggio_andata} min
                          {selectedEvent.extendedProps.tempo_viaggio_ritorno && ` (ritorno: ${selectedEvent.extendedProps.tempo_viaggio_ritorno} min)`}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Tecnici */}
                {selectedEvent.extendedProps.tecnici && selectedEvent.extendedProps.tecnici.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                        <GroupIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Tecnici Assegnati
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {selectedEvent.extendedProps.tecnici.map((tecnico) => (
                            <Chip
                              key={tecnico.id}
                              label={tecnico.nome}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                {/* Descrizione Lavori */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(229, 0, 0, 0.1)', color: '#e50000', width: 36, height: 36 }}>
                      <NotesIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Descrizione Lavori
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {selectedEvent.extendedProps.descrizione_lavori || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Note Operative */}
                {selectedEvent.extendedProps.note_operative && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', width: 36, height: 36 }}>
                        <AssignmentIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Note Operative
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          {selectedEvent.extendedProps.note_operative}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Indicazioni stradali">
                <IconButton
                  onClick={handleGetDirections}
                  sx={{
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                    color: '#2196f3',
                    '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.2)' },
                  }}
                >
                  <DirectionsIcon />
                </IconButton>
              </Tooltip>

              <Box sx={{ flex: 1 }} />

              <Button
                onClick={handleViewIntervento}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: '#e50000',
                  color: '#e50000',
                  '&:hover': { borderColor: '#c50000', bgcolor: 'rgba(229, 0, 0, 0.05)' },
                }}
              >
                Visualizza Dettaglio
              </Button>

              {canManage && (
                <Button
                  onClick={handleGestisciTimbrature}
                  variant="contained"
                  startIcon={<AccessTimeIcon />}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#9c27b0',
                    '&:hover': { bgcolor: '#7b1fa2' },
                  }}
                >
                  Timbrature
                </Button>
              )}

              {isAdmin && (
                <Button
                  onClick={handleEditIntervento}
                  variant="contained"
                  startIcon={<EditIcon />}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#e50000',
                    '&:hover': { bgcolor: '#c50000' },
                  }}
                >
                  Modifica
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Calendario;
